import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faHeartPulse, faPersonWalking, faBed, faDroplet, faUtensils, 
    faListCheck, faAppleWhole, faDna, faWeightScale, faRulerVertical,
    faCalculator, faFlask, faBacteria, faMicroscope, faVial, faTablets
} from '@fortawesome/free-solid-svg-icons';
import './StepStyles.css';

/**
 * Specialized component for rendering Step 1 data: Data Availability Summary
 */
const Step1 = ({ data }) => {
    // Ensure data exists
    if (!data || !data.length || !data[0]) {
        return <div className="s1-empty-state">No data available</div>;
    }

    const stepData = data[0]; // API returns array, even for single items
    const { dataAvailability, basicInfo } = stepData;

    // Split data into categories
    const healthMetrics = ['Heart Rate', 'Activity (Steps)', 'Sleep Records', 'Water Intake'];
    const nutritionMetrics = ['Nutrition (Macros)', 'Nutrition (Meals Logged)', 'Habit Tracking'];
    
    // Group data by category
    const getDataByCategory = (categoryNames) => {
        return Object.entries(dataAvailability || {})
            .filter(([name, info]) => categoryNames.includes(name) && info.available);
    };
    
    // Extract Omics data
    const omicsData = dataAvailability?.['Omics Reports'];
    const omicsReports = omicsData?.details ? Object.entries(omicsData.details) : [];

    return (
        <div className="s1-container">
            {/* Section 1: Basic Info */}
            {basicInfo && (
                <div className="s1-section">
                    <div className="s1-section-header">
                        <FontAwesomeIcon icon={faWeightScale} className="s1-section-icon" />
                        <span>Patient Profile</span>
                    </div>
                    <div className="s1-basic-info">
                        <div className="s1-patient-name">
                            {basicInfo.firstName} {basicInfo.lastName}
                            <span className="s1-patient-id">{stepData.patientId}</span>
                        </div>
                        <div className="s1-metrics-row">
                            {basicInfo.Age && (
                                <div className="s1-metric-pill">
                                    <span className="s1-metric-value">{basicInfo.Age}</span>
                                    <span className="s1-metric-label">years</span>
                                </div>
                            )}
                            {basicInfo.Sex && (
                                <div className="s1-metric-pill">
                                    <span className="s1-metric-label">{basicInfo.Sex}</span>
                                </div>
                            )}
                            {basicInfo.Weight_kg && (
                                <div className="s1-metric-pill">
                                    <FontAwesomeIcon icon={faWeightScale} className="s1-metric-icon" />
                                    <span className="s1-metric-value">{basicInfo.Weight_kg}</span>
                                    <span className="s1-metric-label">kg</span>
                                </div>
                            )}
                            {basicInfo.Height_cm && (
                                <div className="s1-metric-pill">
                                    <FontAwesomeIcon icon={faRulerVertical} className="s1-metric-icon" />
                                    <span className="s1-metric-value">{basicInfo.Height_cm}</span>
                                    <span className="s1-metric-label">cm</span>
                                </div>
                            )}
                            {basicInfo.BMI && (
                                <div className="s1-metric-pill">
                                    <FontAwesomeIcon icon={faCalculator} className="s1-metric-icon" />
                                    <span className="s1-metric-value">{basicInfo.BMI}</span>
                                    <span className="s1-metric-label">BMI</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Section 2: Health Metrics */}
            <div className="s1-section">
                <div className="s1-section-header">
                    <FontAwesomeIcon icon={faHeartPulse} className="s1-section-icon" />
                    <span>Health Metrics</span>
                </div>
                <div className="s1-data-grid">
                    {getDataByCategory(healthMetrics).map(([category, info]) => (
                        <div key={category} className="s1-data-item">
                            <div className="s1-data-icon">
                                <FontAwesomeIcon icon={getIconForCategory(category)} />
                            </div>
                            <div className="s1-data-info">
                                <div className="s1-data-name">{getShortName(category)}</div>
                                {info.count && <div className="s1-data-count">{info.count} records</div>}
                            </div>
                            <div className="s1-hover-details">
                                <div className="s1-detail-header">{category}</div>
                                {info.dateRange && <div className="s1-detail-row">Period: {info.dateRange}</div>}
                                {info.count && <div className="s1-detail-row">Records: {info.count}</div>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Section 3: Nutrition & Habits */}
            <div className="s1-section">
                <div className="s1-section-header">
                    <FontAwesomeIcon icon={faAppleWhole} className="s1-section-icon" />
                    <span>Nutrition & Habits</span>
                </div>
                <div className="s1-data-grid">
                    {getDataByCategory(nutritionMetrics).map(([category, info]) => (
                        <div key={category} className="s1-data-item">
                            <div className="s1-data-icon">
                                <FontAwesomeIcon icon={getIconForCategory(category)} />
                            </div>
                            <div className="s1-data-info">
                                <div className="s1-data-name">{getShortName(category)}</div>
                                {info.count && <div className="s1-data-count">{info.count} records</div>}
                            </div>
                            <div className="s1-hover-details">
                                <div className="s1-detail-header">{category}</div>
                                {info.dateRange && <div className="s1-detail-row">Period: {info.dateRange}</div>}
                                {info.count && <div className="s1-detail-row">Records: {info.count}</div>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Section 4: Omics Reports */}
            {omicsData && omicsData.available && (
                <div className="s1-section">
                    <div className="s1-section-header">
                        <FontAwesomeIcon icon={faDna} className="s1-section-icon" />
                        <span>Omics Reports</span>
                    </div>
                    <div className="s1-omics-grid">
                        {omicsReports.map(([reportName, reportInfo]) => (
                            <div key={reportName} className="s1-omics-card">
                                <div className="s1-omics-icon">
                                    <FontAwesomeIcon icon={getOmicsIcon(reportName)} />
                                </div>
                                <div className="s1-omics-info">
                                    <div className="s1-omics-name">{reportName}</div>
                                    <div className="s1-omics-meta">
                                        {reportInfo.count} file{reportInfo.count !== 1 ? 's' : ''}
                                    </div>
                                </div>
                                {reportInfo.files && reportInfo.files.length > 0 && (
                                    <div className="s1-omics-files">
                                        {reportInfo.files.map((file, index) => (
                                            <div key={index} className="s1-file-tag">{file}</div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// Return a shortened version of category names
function getShortName(category) {
    switch(category) {
        case 'Activity (Steps)': return 'Steps';
        case 'Sleep Records': return 'Sleep';
        case 'Water Intake': return 'Water';
        case 'Heart Rate': return 'Heart';
        case 'Habit Tracking': return 'Habits';
        case 'Nutrition (Macros)': return 'Macros';
        case 'Nutrition (Meals Logged)': return 'Meals';
        case 'Omics Reports': return 'Omics';
        default: return category;
    }
}

// Return Font Awesome icon for category
function getIconForCategory(category) {
    switch(category) {
        case 'Activity (Steps)': return faPersonWalking;
        case 'Sleep Records': return faBed;
        case 'Water Intake': return faDroplet;
        case 'Heart Rate': return faHeartPulse;
        case 'Habit Tracking': return faListCheck;
        case 'Nutrition (Macros)': return faUtensils;
        case 'Nutrition (Meals Logged)': return faAppleWhole;
        case 'Omics Reports': return faDna;
        default: return faFlask;
    }
}

// Return Font Awesome icon for Omics reports
function getOmicsIcon(reportType) {
    switch(reportType) {
        case 'WGS': return faDna;
        case 'Gut Health': return faBacteria;
        case 'Proteomics': return faFlask;
        case 'Oral Health': return faBacteria;
        case 'Transcriptome': return faMicroscope;
        case 'Metabolome Urine': 
        case 'Metabolome Lipids':
        case 'Metabolome Plasma': return faVial;
        default: return faTablets;
    }
}

export default Step1;
